import api from './api';

export interface Unit {
  id: number;
  projectId: number;
  unitType: string; // SHOP, 3BHK, 4BHK
  unitNo: string; // A-101, B-403
  wing: string; // A, B, C
  size: string; // 330 Sq Yards
  currentStatus: string; // sold, unsold, approval_pending, hold
  saleDeedAmount?: number;
  extraWorkAmount?: number;
  terraceStatus?: string;
  terraceSize?: string;
  extraSpace?: string;
  extraSpaceAmount?: number;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: number;
    name: string;
  };
}

export interface CreateUnitData {
  unitType: string;
  unitNo: string;
  wing: string;
  size: string;
  currentStatus: string;
  saleDeedAmount?: number;
  extraWorkAmount?: number;
  terraceStatus?: string;
  terraceSize?: string;
  extraSpace?: string;
  extraSpaceAmount?: number;
}

export interface UpdateUnitData extends CreateUnitData {}

export interface UnitsResponse {
  units: Unit[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface GetUnitsParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export const unitService = {
  // Get units for a project with pagination
  getUnits: async (projectId: number, params: GetUnitsParams = {}): Promise<UnitsResponse> => {
    const response = await api.get(`/projects/${projectId}/units`, { params });
    return response.data;
  },

  // Get single unit by ID
  getUnit: async (id: number): Promise<Unit> => {
    const response = await api.get(`/units/${id}`);
    return response.data;
  },

  // Create new unit
  createUnit: async (projectId: number, data: CreateUnitData): Promise<Unit> => {
    const response = await api.post(`/projects/${projectId}/units`, data);
    return response.data;
  },

  // Update unit
  updateUnit: async (id: number, data: UpdateUnitData): Promise<Unit> => {
    const response = await api.put(`/units/${id}`, data);
    return response.data;
  },

  // Delete unit
  deleteUnit: async (id: number): Promise<void> => {
    await api.delete(`/units/${id}`);
  },

  // Bulk upload units via CSV
  bulkUploadUnits: async (projectId: number, units: CreateUnitData[]): Promise<Unit[]> => {
    const response = await api.post(`/projects/${projectId}/units/bulk-upload`, { units });
    return response.data;
  },

  // Download CSV template
  // Get all units (for dropdowns and searches)
  getAllUnits: async (): Promise<Unit[]> => {
    try {
      // Get units from all projects
      const response = await api.get('/units/all');
      return response.data;
    } catch (error) {
      // Fallback: get from projects and aggregate
      const projectsResponse = await api.get('/projects');
      const projects = projectsResponse.data;
      
      const allUnits: Unit[] = [];
      for (const project of projects) {
        const unitsResponse = await unitService.getUnits(project.id, { page: 1, limit: 1000 });
        allUnits.push(...unitsResponse.units);
      }
      return allUnits;
    }
  },

  // Get unique wings for a project
  getWingsByProject: async (projectId: number): Promise<string[]> => {
    try {
      const response = await unitService.getUnits(projectId, { page: 1, limit: 1000 });
      const wingsSet = new Set<string>();
      response.units.forEach(unit => wingsSet.add(unit.wing));
      const wings = Array.from(wingsSet);
      return wings.sort((a, b) => a.localeCompare(b));
    } catch (error) {
      console.error('Error fetching wings:', error);
      return [];
    }
  },

  // Get unique sizes for a project
  getSizesByProject: async (projectId: number): Promise<string[]> => {
    try {
      const response = await unitService.getUnits(projectId, { page: 1, limit: 1000 });
      const sizesSet = new Set<string>();
      response.units.forEach(unit => {
        if (unit.size && unit.size.trim()) sizesSet.add(unit.size.trim());
      });
      const sizes = Array.from(sizesSet);
      return sizes.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    } catch (error) {
      console.error('Error fetching sizes:', error);
      return [];
    }
  },

  downloadTemplate: async () => {
    const response = await api.get('/units/template/download', {
      responseType: 'blob'
    });
    return response.data;
  }
};
